"use client";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowLeft, FaArrowRight, FaLinkedin } from "react-icons/fa";
import { useTheme } from "../../Components/ThemeContext";

const lightGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

const darkGridStyle = {
  backgroundImage: `
    linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)
  `,
  backgroundSize: "40px 40px",
};

// ✅ SEO: Each member has name, role, bio, and LinkedIn for EEAT signals
const teamMembers = [
  {
    name: "Muhammad Umar",
    role: "Chief Executive Officer",
    img: "/Images/team1.png",
    linkedin: "https://www.linkedin.com/in/muhammad-umar-b599953a8/",
  },
  {
    name: "Ali Mushtaq",
    role: "Chief Technology Officer",
    img: "/Images/ali.jpg",
    linkedin: "https://www.linkedin.com/in/ali-mushtaq-258b3930b/",
  },
  {
    name: "Mujahid Mushtaq",
    role: "Chief Operating Officer",
    img: "/Images/mujahid.jpg",
    linkedin: "https://www.linkedin.com/in/mujahid-mushtaq-3ba479255/",
  },
];

function useVisibleCount() {
  const [count, setCount] = React.useState(1);
  React.useEffect(() => {
    const update = () => {
      if (window.innerWidth >= 1024) setCount(3);
      else if (window.innerWidth >= 640) setCount(2);
      else setCount(1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return count;
}

export default function TeamSection() {
  const { isDark } = useTheme();
  const [startIndex, setStartIndex] = useState(0);
  const visibleCount = useVisibleCount();

  const showArrows = teamMembers.length > visibleCount;
  const maxIndex = Math.max(0, teamMembers.length - visibleCount);

  useEffect(() => {
    if (startIndex > maxIndex) setStartIndex(maxIndex);
  }, [maxIndex, startIndex]);

  const prev = () => setStartIndex((i) => Math.max(0, i - 1));
  const next = () => setStartIndex((i) => Math.min(maxIndex, i + 1));

  const visibleMembers = teamMembers.slice(startIndex, startIndex + visibleCount);

  const greenHeading = isDark ? "text-green-400" : "text-green-700";
  const headingText = isDark ? "text-white" : "text-gray-900";
  const descText = isDark ? "text-gray-300" : "text-gray-600";
  const cardBg = isDark ? "bg-gray-900" : "bg-[#1a2e1a]";

  return (
    // ✅ SEO: section with aria-labelledby for screen readers & Google
    <section
      aria-labelledby="team-section-heading"
      className={`relative w-full overflow-hidden pt-0 pb-12 px-4 sm:px-8 md:px-12 lg:px-20 transition-colors duration-300 ${
        isDark ? "bg-black text-white" : "bg-transparent text-gray-900"
      }`}
      style={isDark ? darkGridStyle : lightGridStyle}
    >
      {!isDark && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }}>
          <div
            style={{
              position: "absolute",
              top: "5%",
              left: "-8%",
              width: "45vw",
              height: "45vw",
              background: "radial-gradient(circle, rgba(74,222,128,0.10) 0%, transparent 70%)",
              filter: "blur(45px)",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "10%",
              right: "-10%",
              width: "40vw",
              height: "40vw",
              background: "radial-gradient(circle, rgba(251,146,60,0.08) 0%, transparent 70%)",
              filter: "blur(45px)",
            }}
          />
        </div>
      )}

      <div className="relative z-10">
        {/* ✅ SEO: Keyword-rich headings matching SRS section 2.7 */}
        <div className="text-center mb-8 sm:mb-10">
          <p className={`text-sm font-semibold uppercase tracking-widest ${greenHeading} mb-1`}>
            The People Behind Fiieve
          </p>
          <h2
            id="team-section-heading"
            className={`text-2xl sm:text-3xl md:text-4xl font-bold ${headingText} mb-3`}
          >
            Meet Our{" "}
            <span className={greenHeading}>Expert Team</span>
          </h2>
          <p className={`mx-auto max-w-2xl text-sm md:text-base ${descText}`}>
            Our team of skilled engineers and designers combines software, AI, and hardware expertise to deliver end-to-end digital solutions for startups and enterprises.
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 sm:gap-4 max-w-5xl mx-auto">
          {showArrows && (
            <button
              onClick={prev}
              disabled={startIndex === 0}
              aria-label="View previous team members"
              className="bg-green-400 text-black p-2 sm:p-3 rounded-full hover:bg-green-300 transition-all shadow-lg flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <FaArrowLeft size={14} />
            </button>
          )}

          <div className="overflow-visible flex-1 py-6">
            <AnimatePresence mode="popLayout">
              <motion.div
                key={`${startIndex}-${visibleCount}`}
                className="flex justify-center gap-4 sm:gap-6 lg:gap-8"
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              >
                {visibleMembers.map((member, i) => (
                  // ✅ SEO: article element signals this is an independent content unit (person)
                  <article
                    key={startIndex + i}
                    aria-label={`${member.name} — ${member.role} at Fiieve`}
                    className={`${cardBg} border border-green-400/40 rounded-2xl p-4 sm:p-5 md:p-6 text-center flex-shrink-0 transition-all duration-300 hover:scale-105 shadow-[0_0_25px_rgba(16,185,129,0.15)] hover:shadow-[0_0_35px_rgba(16,185,129,0.4)]`}
                    style={{
                      width:
                        visibleCount === 1
                          ? "min(240px, 72vw)"
                          : visibleCount === 2
                          ? "min(200px, 42vw)"
                          : "min(270px, 28vw)",
                    }}
                  >
                    {/* ✅ SEO: descriptive alt text with name + role + company */}
                    <div className="w-full h-48 sm:h-56 md:h-64 rounded-xl overflow-hidden mb-4 border border-green-400/30">
                      <img
                        src={member.img}
                        alt={`${member.name} — ${member.role} at Fiieve, software development company`}
                        className="w-full h-full object-cover object-top"
                        loading="lazy"
                      />
                    </div>

                    {/* Name */}
                    <h3 className="text-base sm:text-lg font-bold text-white">
                      {member.name}
                    </h3>

                    {/* Role */}
                    <p className="text-green-400 text-xs sm:text-sm font-semibold mt-1 mb-3">
                      {member.role}
                    </p>

                    {/* LinkedIn */}
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Connect with ${member.name} on LinkedIn`}
                      className="inline-flex items-center justify-center mt-1 w-9 h-9 rounded-full bg-green-400 hover:bg-green-300 text-black transition-all duration-300 shadow-md"
                    >
                      <FaLinkedin size={16} aria-hidden="true" />
                    </a>
                  </article>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {showArrows && (
            <button
              onClick={next}
              disabled={startIndex === maxIndex}
              aria-label="View next team members"
              className="bg-green-400 text-black p-2 sm:p-3 rounded-full hover:bg-green-300 transition-all shadow-lg flex-shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <FaArrowRight size={14} />
            </button>
          )}
        </div>

        {showArrows && (
          <div className="flex justify-center mt-2 gap-2 sm:gap-3" role="tablist" aria-label="Team member navigation dots">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setStartIndex(i)}
                role="tab"
                aria-selected={i === startIndex}
                aria-label={`Go to team member group ${i + 1}`}
                className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                  i === startIndex ? "bg-green-400" : "bg-gray-600"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}